<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div class="report-wrapper">

      <!-- Filters -->
      <div class="table-header-filters">
        <v-row dense class="align-center">

          <v-col class="date-col">
            <v-menu v-model="reportFromMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field class="filter-select" :value="formatDateFrom" label="Od"
                  dense outlined hide-details prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" />
              </template>
              <v-date-picker v-model="dateFrom" no-title scrollable @input="reportFromMenu = false" />
            </v-menu>
          </v-col>

          <v-col class="date-col">
            <v-menu v-model="reportToMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field class="filter-select" :value="formatDateTo" label="Do"
                  dense outlined hide-details prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" />
              </template>
              <v-date-picker v-model="dateTo" no-title scrollable @input="reportToMenu = false" />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-autocomplete class="filter-select" dense outlined hide-details label="Prodajno mjesto"
              v-model="location" :items="locations" item-text="name" item-value="id" return-object
              @change="onLocationChange" />
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-autocomplete class="filter-select" dense outlined hide-details label="KDS stanica (sve)"
              v-model="selectedUnit" :items="units" item-text="name" item-value="id" return-object
              clearable :disabled="!location" />
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-autocomplete class="filter-select" dense outlined hide-details label="Konobar (svi)"
              v-model="waiterSearch" :items="availableWaiters" item-text="name" item-value="id" return-object
              clearable prepend-inner-icon="mdi-account" no-data-text="Nema konobara" />
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-autocomplete class="filter-select" dense outlined hide-details label="Pretraži jelo"
              v-model="selectedDish" :items="dishResults" :loading="dishSearchLoading"
              :search-input.sync="dishSearchText" item-text="name" item-value="id"
              return-object clearable prepend-inner-icon="mdi-food" no-data-text="Unesite naziv jela" />
          </v-col>

          <v-col cols="auto" class="d-flex align-center" style="gap:8px">
            <v-btn :disabled="!location" class="search-btn" @click="getData()" small>
              <v-icon small class="mr-1">mdi-magnify</v-icon>
              Pretraži
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon class="close-btn" :to="closeTo" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>

        </v-row>
      </div>

      <!-- Report title -->
      <div v-if="searched" class="report-title-wrapper">
        <p class="report-main-title">KDS analitika</p>
        <p class="report-subtitle">za razdoblje od {{ formatDateFrom }} do {{ formatDateTo }}</p>
      </div>

      <!-- Summary cards -->
      <v-row v-if="filteredOrders.length" dense class="mb-2">
        <v-col cols="6" sm="3">
          <div class="summary-card">
            <div class="summary-label">Ukupno narudžbi</div>
            <div class="summary-value">{{ orders.length }}</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="summary-card">
            <div class="summary-label">S vremenom završetka</div>
            <div class="summary-value">{{ completedOrders.length }}</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="summary-card">
            <div class="summary-label">Prosječno trajanje</div>
            <div class="summary-value">{{ avgOrderDuration }}</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="summary-card">
            <div class="summary-label">Najdulje / najkraće</div>
            <div class="summary-value">{{ maxOrderDuration }} / {{ minOrderDuration }}</div>
          </div>
        </v-col>
      </v-row>

      <!-- Tabs -->
      <v-tabs v-if="filteredOrders.length" v-model="activeTab" class="mt-2" color="#00BCD4">
        <v-tab>Narudžbe</v-tab>
        <v-tab>Analiza jela</v-tab>
      </v-tabs>

      <!-- Tab: Orders grouped by table → session -->
      <div v-if="filteredOrders.length && activeTab === 0" class="mt-2">
        <v-expansion-panels accordion multiple>
          <v-expansion-panel v-for="tg in tableGroups" :key="tg.tableId">
            <v-expansion-panel-header class="table-panel-header">
              <div class="d-flex align-center" style="gap:12px">
                <v-icon color="#00BCD4" small>mdi-table-chair</v-icon>
                <span class="font-weight-bold">{{ tg.tableName }}</span>
                <span class="grey--text text--darken-1" style="font-size:12px">
                  {{ tg.totalOrders }} narudžbi · {{ tg.sessions.length }} {{ tg.sessions.length === 1 ? 'posjeta' : 'posjete' }}
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-for="(session, si) in tg.sessions" :key="si" class="session-block">
                <div class="session-label">
                  <v-icon x-small color="#6B7280" class="mr-1">mdi-account-group</v-icon>
                  Posjeta {{ si + 1 }}
                  <span class="ml-2 grey--text" style="font-weight:400">{{ formatTs(session.startTime) }}</span>
                  <span v-if="session.allDone" class="ml-2 badge-done" style="font-size:10px">Sve završeno</span>
                  <span v-else class="ml-2 badge-pending" style="font-size:10px">U tijeku</span>
                </div>
                <table class="report-table session-table">
                  <thead>
                    <tr>
                      <th style="width:32px"></th>
                      <th>Br.</th>
                      <th>Konobar</th>
                      <th>Stanica</th>
                      <th>Trajanje</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(o, oi) in session.orders">
                      <tr :key="`o-${oi}`" class="order-row" @click="toggleOrder(o._id)">
                        <td>
                          <v-icon x-small color="#9CA3AF">
                            {{ expandedOrders[o._id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                          </v-icon>
                        </td>
                        <td>#{{ o.order_number }}</td>
                        <td>{{ o.added_by_name || '—' }}</td>
                        <td>{{ o.unit_name }}</td>
                        <td>{{ orderDuration(o) }}</td>
                        <td>
                          <span :class="o.done_at ? 'badge-done' : 'badge-pending'">
                            {{ o.done_at ? 'Završeno' : 'U tijeku' }}
                          </span>
                        </td>
                      </tr>
                      <tr v-if="expandedOrders[o._id]" :key="`d-${oi}`" class="order-detail-row">
                        <td colspan="6" style="padding:0">
                          <table class="items-table">
                            <thead>
                              <tr>
                                <th>Jelo</th>
                                <th>Kom.</th>
                                <th>Trajanje (ukupno)</th>
                                <th>Trajanje (po kom.)</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(item, ii) in orderItemDetails(o)" :key="ii">
                                <td>{{ item.name }}</td>
                                <td>{{ item.qty }}</td>
                                <td>{{ item.totalDuration }}</td>
                                <td>{{ item.unitDuration }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <!-- Tab: Dish analysis -->
      <div v-if="filteredOrders.length && activeTab === 1" class="glass-table-wrapper mt-2">
        <table class="report-table">
          <thead>
            <tr>
              <th>Jelo</th>
              <th>Broj pripremanja</th>
              <th>Prosječno</th>
              <th>Najkraće</th>
              <th>Najdulje</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(d, i) in dishStats" :key="i">
              <td>{{ d.name }}</td>
              <td>{{ d.count }}</td>
              <td>{{ formatMin(d.avg) }}</td>
              <td>{{ formatMin(d.min) }}</td>
              <td>{{ formatMin(d.max) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="searched && !filteredOrders.length" class="text-center mt-8 grey--text">
        Nema podataka za odabrani period.
      </div>

    </div>
  </v-container>
</template>

<script>
import { df, auth } from '@/plugins/firebase'
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import triGram from '@/mixins/trigram'

export default {
  name: 'KdsReport',
  mixins: [triGram],
  inject: ['showLoader', 'hideLoader'],
  data () {
    const today = new Date().toISOString().slice(0, 10)
    return {
      margin: '0px',
      targetModifier: '',
      dateFrom: today,
      dateTo: today,
      reportFromMenu: false,
      reportToMenu: false,
      location: null,
      locations: [],
      selectedUnit: null,
      units: [],
      orders: [],
      selectedDish: null,
      dishResults: [],
      dishSearchText: '',
      dishSearchLoading: false,
      waiterSearch: null,
      expandedOrders: {},
      activeTab: 0,
      searched: false
    }
  },
  watch: {
    dishSearchText (val) {
      clearTimeout(this._dishTimer)
      if (!val || val.length < 2) return
      this._dishTimer = setTimeout(() => this.searchDishes(val), 300)
    }
  },
  async created () {
    await this.getPointsOfSale()
  },
  mounted () {
    this.margin = state.getMargin()
    const sideNav = this.$route.meta.sideNav
    if (sideNav === 'Company') this.targetModifier = 'company'
    else if (sideNav === 'PointOfSale' || sideNav === 'CashRegister') this.targetModifier = 'pointOfSale'
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
  },
  computed: {
    closeTo () {
      return { name: `${this.targetModifier}.all-reports.reportMenu` }
    },
    formatDateFrom () {
      return this.dateFrom ? new Intl.DateTimeFormat('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(this.dateFrom)) : ''
    },
    formatDateTo () {
      return this.dateTo ? new Intl.DateTimeFormat('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(this.dateTo)) : ''
    },
    availableWaiters () {
      const map = {}
      for (const order of this.orders) {
        if (order.added_by_id && !map[order.added_by_id]) {
          map[order.added_by_id] = { id: order.added_by_id, name: order.added_by_name || order.added_by_id }
        }
      }
      return Object.values(map).sort((a, b) => a.name.localeCompare(b.name))
    },
    filteredOrders () {
      if (!this.waiterSearch) return this.orders
      return this.orders.filter(o => o.added_by_id === this.waiterSearch.id)
    },
    completedOrders () {
      return this.filteredOrders.filter(o => o.done_at)
    },
    tableGroups () {
      // Group by table
      const byTable = {}
      for (const order of this.filteredOrders) {
        const key = order.table_id || order.table_name || '?'
        if (!byTable[key]) byTable[key] = { tableId: key, tableName: order.table_name || key, orders: [] }
        byTable[key].orders.push(order)
      }
      return Object.values(byTable)
        .map(tg => {
          // Sort orders by creation time ascending
          const sorted = [...tg.orders].sort((a, b) =>
            (a.order_created?.seconds ?? 0) - (b.order_created?.seconds ?? 0)
          )
          // Split into sessions: new session starts when order_number == 1
          const sessions = []
          let current = null
          for (const order of sorted) {
            if (order.order_number === 1 || current === null) {
              current = { startTime: order.order_created, orders: [] }
              sessions.push(current)
            }
            current.orders.push(order)
          }
          // Mark session as fully done if all its orders have done_at
          sessions.forEach(s => { s.allDone = s.orders.every(o => !!o.done_at) })
          return { ...tg, sessions, totalOrders: tg.orders.length }
        })
        .sort((a, b) => a.tableName.localeCompare(b.tableName, undefined, { numeric: true, sensitivity: 'base' }))
    },
    sortedOrders () {
      return [...this.filteredOrders].sort((a, b) => {
        const ta = a.order_created?.seconds ?? 0
        const tb = b.order_created?.seconds ?? 0
        return tb - ta
      })
    },
    avgOrderDuration () {
      if (!this.completedOrders.length) return '—'
      const sum = this.completedOrders.reduce((acc, o) => acc + this._durationSec(o), 0)
      return this.formatMin(sum / this.completedOrders.length)
    },
    maxOrderDuration () {
      if (!this.completedOrders.length) return '—'
      return this.formatMin(Math.max(...this.completedOrders.map(o => this._durationSec(o))))
    },
    minOrderDuration () {
      if (!this.completedOrders.length) return '—'
      return this.formatMin(Math.min(...this.completedOrders.map(o => this._durationSec(o))))
    },
    dishStats () {
      const map = {}
      // qty > 1 means multiple units were done together (e.g. 3x pizza in 30 min = 10 min/unit).
      // total += sec, count += qty → avg = sec / qty per unit.
      const accumulate = (name, sec, qty = 1) => {
        if (!name || sec <= 0) return
        const secPerUnit = sec / qty
        if (!map[name]) map[name] = { name, count: 0, total: 0, min: Infinity, max: -Infinity }
        map[name].count += qty
        map[name].total += sec
        if (secPerUnit < map[name].min) map[name].min = secPerUnit
        if (secPerUnit > map[name].max) map[name].max = secPerUnit
      }
      for (const order of this.filteredOrders) {
        const createdSec = order.order_created?.seconds
        if (!createdSec) continue
        // Build product_id → quantity lookup from the items snapshot
        const qtyById = {}
        for (const item of (order.items || [])) {
          if (item.product_id) qtyById[item.product_id] = item.quantity ?? 1
        }
        const doneEvents = (order.item_events || []).filter(e => e.type === 'done')
        const fallbackSec = order.done_at?.seconds ? order.done_at.seconds - createdSec : 0
        if (doneEvents.length > 0) {
          for (const event of doneEvents) {
            const qty = qtyById[event.product_id] ?? 1
            const raw = (event.ts?.seconds ?? 0) - createdSec
            accumulate(event.product_name || '?', raw > 0 ? raw : fallbackSec, qty)
          }
        } else if (order.done_at?.seconds) {
          const sec = order.done_at.seconds - createdSec
          for (const item of (order.items || [])) {
            accumulate(item.product_name || item.name || '?', sec, item.quantity ?? 1)
          }
        }
      }
      return Object.values(map)
        .map(d => ({ ...d, avg: d.total / d.count }))
        .sort((a, b) => b.avg - a.avg)
    }
  },
  methods: {
    async searchDishes (searchTerm) {
      this.dishSearchLoading = true
      try {
        const trigramMap = this.triGram(searchTerm)
        let q = df.collection(`companies/${state.getCurrentCompany().id}/items`)
          .where('status', '==', 'OK')
        Object.keys(trigramMap).forEach(key => {
          q = q.where(`meta.${key}`, '==', true)
        })
        const snap = await q.limit(15).get()
        this.dishResults = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => a.name.localeCompare(b.name))
      } catch (e) {
        console.error('Dish search failed:', e)
      } finally {
        this.dishSearchLoading = false
      }
    },
    _durationSec (order) {
      return (order.done_at?.seconds ?? 0) - (order.order_created?.seconds ?? 0)
    },
    formatMin (seconds) {
      if (!seconds || seconds <= 0) return '—'
      const m = Math.floor(seconds / 60)
      const s = Math.round(seconds % 60)
      return m > 0 ? `${m}m ${s}s` : `${s}s`
    },
    formatTs (ts) {
      if (!ts?.seconds) return '—'
      return new Date(ts.seconds * 1000).toLocaleString('hr-HR', { dateStyle: 'short', timeStyle: 'short' })
    },
    orderDuration (order) {
      if (!order.done_at) return '—'
      return this.formatMin(this._durationSec(order))
    },
    toggleOrder (id) {
      this.$set(this.expandedOrders, id, !this.expandedOrders[id])
    },
    orderItemDetails (order) {
      const createdSec = order.order_created?.seconds
      if (!createdSec) return []
      const fallbackSec = order.done_at?.seconds ? order.done_at.seconds - createdSec : 0
      const qtyById = {}
      for (const item of (order.items || [])) {
        if (item.product_id) qtyById[item.product_id] = item.quantity ?? 1
      }
      const doneEvents = (order.item_events || []).filter(e => e.type === 'done')
      if (doneEvents.length > 0) {
        return doneEvents.map(e => {
          const qty = qtyById[e.product_id] ?? 1
          // item_events use device clock; fall back to done_at if ts is bad
          const raw = (e.ts?.seconds ?? 0) - createdSec
          const sec = raw > 0 ? raw : fallbackSec
          return {
            name: e.product_name || '?',
            qty,
            totalDuration: this.formatMin(sec),
            unitDuration: qty > 1 ? this.formatMin(sec / qty) : '—'
          }
        })
      } else if (fallbackSec > 0) {
        return (order.items || []).map(item => {
          const qty = item.quantity ?? 1
          return {
            name: item.product_name || item.name || '?',
            qty,
            totalDuration: this.formatMin(fallbackSec),
            unitDuration: qty > 1 ? this.formatMin(fallbackSec / qty) : '—'
          }
        })
      }
      return (order.items || []).map(item => ({
        name: item.product_name || item.name || '?', qty: item.quantity ?? 1, totalDuration: '—', unitDuration: '—'
      }))
    },
    async getPointsOfSale () {
      if (!auth.currentUser) return
      const docRef = df.doc(`user_locations/${auth.currentUser.uid}.${state.getCurrentCompany().id}`)
      docRef.onSnapshot(doc => {
        if (!doc.exists || !doc.data()?.locations) return
        this.locations = Object.values(doc.data().locations).sort((a, b) => a.name.localeCompare(b.name))
      })
    },
    async onLocationChange () {
      this.selectedUnit = null
      this.waiterSearch = null
      this.selectedDish = null
      this.dishResults = []
      this.units = []
      if (!this.location?.id) return
      const snap = await df.collection(`location_units/${this.location.id}/units`).get()
      this.units = snap.docs.map(d => ({ id: d.id, name: d.data().name || d.id }))
      this.units.sort((a, b) => a.name.localeCompare(b.name))
    },
    async getData () {
      if (!this.location?.id) return
      this.showLoader()
      try {
        let query = df.collection(`kds_logs/${this.location.id}/orders`)
          .where('date', '>=', this.dateFrom)
          .where('date', '<=', this.dateTo)
        if (this.selectedUnit?.id) {
          query = query.where('unit_id', '==', this.selectedUnit.id)
        }
        if (this.selectedDish?.id) {
          query = query.where('product_ids', 'array-contains', this.selectedDish.id)
        }
        const snap = await query.get()
        this.orders = snap.docs.map(d => ({ _id: d.id, ...d.data() }))
        this.expandedOrders = {}
        this.searched = true
        this.activeTab = 0
      } finally {
        this.hideLoader()
      }
    }
  }
}
</script>

<style scoped>
.report-wrapper {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

/* ── Date picker columns (fixed narrow width) ──────────────────────── */
.date-col {
  flex: 0 0 145px !important;
  max-width: 145px !important;
  padding: 4px !important;
}

/* ── Filter header ─────────────────────────────────────────────────── */
.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #ffffff;
  padding: 16px;
  margin-bottom: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
}

/* Input field borders */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}
.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}
.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}
.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}
.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
}
.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

/* Buttons */
.search-btn {
  height: 36px !important;
  padding: 0 14px !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}
.search-btn:hover:not(:disabled) {
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
}
.search-btn:disabled { opacity: 0.45 !important; }

.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}
.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

/* ── Report title ──────────────────────────────────────────────────── */
.report-title-wrapper { margin: 28px 0 12px; }
.report-main-title {
  font-weight: 700;
  font-size: 24px;
  color: #2d3748;
  letter-spacing: 0.3px;
  margin: 0;
  padding: 10px 0 0;
}
.report-subtitle {
  font-weight: 600;
  font-size: 14px;
  color: #666;
  margin: 0;
  padding: 4px 0 0;
}

/* ── Summary cards ─────────────────────────────────────────────────── */
.summary-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  padding: 16px;
  text-align: center;
}
.summary-label { font-size: 12px; color: #6B7280; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
.summary-value { font-size: 22px; font-weight: 700; color: #2d3748; }

/* ── Glass wrapper (dish analysis tab) ────────────────────────────── */
.glass-table-wrapper {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 16px;
  overflow: hidden;
}

/* ── Tables (dish analysis) ───────────────────────────────────────── */
.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.report-table th {
  background: #F8FAFC;
  padding: 12px 20px;
  text-align: left;
  font-weight: 700;
  font-size: 12px;
  color: #2D3748;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-bottom: 1px solid #EDF2F7;
  white-space: nowrap;
}
.report-table td {
  padding: 12px 20px;
  border-bottom: 1px solid #EDF2F7;
  color: #4B5563;
}
.report-table tbody tr:last-child td { border-bottom: none; }
.report-table tbody tr:hover { background: #F7FAFC; }

/* ── Badges ────────────────────────────────────────────────────────── */
.badge-done {
  background: #D1FAE5;
  color: #065F46;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}
.badge-pending {
  background: #FEF3C7;
  color: #92400E;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

/* ── Table expansion panels ────────────────────────────────────────── */
.table-panel-header {
  font-size: 14px;
  font-weight: 600;
  background: #F8FAFC;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

/* ── Session blocks ────────────────────────────────────────────────── */
.session-block {
  margin: 12px 0;
  border-radius: 10px;
  border: 1px solid #EDF2F7;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
}
.session-block:last-child { margin-bottom: 0; }

.session-label {
  display: flex;
  align-items: center;
  padding: 9px 14px;
  background: #F8FAFC;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #EDF2F7;
  letter-spacing: 0.2px;
}

.session-table th {
  background: #ffffff;
  padding: 10px 14px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #6B7280;
  font-weight: 700;
  border-bottom: 1px solid #EDF2F7;
}
.session-table td { padding: 10px 14px; }

/* ── Order rows & item detail ──────────────────────────────────────── */
.order-row { cursor: pointer; }
.order-row:hover { background: #F0FDFF !important; }

.order-detail-row > td { background: #F8FAFC; padding: 0 !important; }

.items-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.items-table th {
  padding: 7px 16px 7px 44px;
  background: #EFF6FF;
  color: #374151;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  text-align: left;
  border-bottom: 1px solid #DBEAFE;
}
.items-table td {
  padding: 6px 16px 6px 44px;
  color: #4B5563;
  border-bottom: 1px solid #EDF2F7;
}
.items-table tbody tr:last-child td { border-bottom: none; }
</style>
